import React from "react";
import {
  X,
  Hash,
  User,
  File,
  MessageSquare,
  Folder,
  Tag as LucideTag,
} from "lucide-react";
import { cn } from "../util";
import type { Tag } from "../types";

interface TagProps {
  tag: Tag;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "outline" | "ghost";
  removable?: boolean;
  interactive?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  className?: string;
}

const sizeClasses = {
  sm: "text-xs px-2 py-1 h-5",
  md: "text-sm px-2.5 py-1.5 h-6",
  lg: "text-sm px-3 py-2 h-8",
};

const variantClasses = {
  default: "bg-primary/10 text-primary border-primary/20 hover:bg-primary/20",
  outline: "bg-transparent border-2 border-border hover:bg-muted",
  ghost: "bg-muted/50 text-muted-foreground border-transparent hover:bg-muted",
};

const getTagIcon = (type: Tag["type"]) => {
  switch (type) {
    case "participant":
      return User;
    case "file":
      return File;
    case "chat-message":
      return MessageSquare;
    case "workspace":
      return Folder;
    case "general":
    default:
      return Hash;
  }
};

const getTagColor = (tag: Tag) => {
  if (tag.color) return tag.color;

  // Default colors based on type
  switch (tag.type) {
    case "participant":
      return "text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100";
    case "file":
      return "text-green-600 bg-green-50 border-green-200 hover:bg-green-100";
    case "chat-message":
      return "text-purple-600 bg-purple-50 border-purple-200 hover:bg-purple-100";
    case "workspace":
      return "text-orange-600 bg-orange-50 border-orange-200 hover:bg-orange-100";
    case "general":
    default:
      return "text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100";
  }
};

export function TagComponent({
  tag,
  size = "md",
  variant = "default",
  removable = false,
  interactive = false,
  onClick,
  onRemove,
  className,
}: TagProps) {
  const IconComponent = tag.icon ? LucideTag : getTagIcon(tag.type);
  const colorClasses = variant === "default" ? getTagColor(tag) : "";

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (interactive && onClick) {
      onClick();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border transition-colors",
        sizeClasses[size],
        variant === "default" ? colorClasses : variantClasses[variant],
        interactive && "cursor-pointer",
        className,
      )}
      onClick={handleClick}
      title={tag.description || `${tag.type}: ${tag.name}`}
    >
      <IconComponent className="w-3 h-3 flex-shrink-0" />
      <span className="truncate max-w-24">{tag.name}</span>
      {removable && (
        <button
          onClick={handleRemove}
          className="ml-1 p-0.5 rounded-sm hover:bg-black/10 transition-colors"
          aria-label={`Remove ${tag.name} tag`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

export interface TagListProps {
  tags: Tag[];
  size?: "sm" | "md" | "lg";
  variant?: "default" | "outline" | "ghost";
  removable?: boolean;
  interactive?: boolean;
  onTagClick?: (tag: Tag) => void;
  onTagRemove?: (tag: Tag) => void;
  className?: string;
  emptyMessage?: string;
}

export function TagList({
  tags,
  size = "md",
  variant = "default",
  removable = false,
  interactive = false,
  onTagClick,
  onTagRemove,
  className,
  emptyMessage = "No tags",
}: TagListProps) {
  if (tags.length === 0) {
    return (
      <span className="text-sm text-muted-foreground italic">
        {emptyMessage}
      </span>
    );
  }

  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {tags.map((tag) => (
        <TagComponent
          key={tag.id}
          tag={tag}
          size={size}
          variant={variant}
          removable={removable}
          interactive={interactive}
          onClick={() => onTagClick?.(tag)}
          onRemove={() => onTagRemove?.(tag)}
        />
      ))}
    </div>
  );
}

// Export with alias for backward compatibility
export { TagComponent as Tag };
